import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { colorOf } from '@/lib/noteColors';
import { StickyNote, CheckSquare, Square, Loader2, HardHat } from 'lucide-react';

export default function NotePublicPage() {
  const { token } = useParams();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/notes/${token}`)
      .then((r) => setNote(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  if (error || !note) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center" data-testid="note-public-error">
        <StickyNote size={40} className="text-slate-300 mb-3" />
        <h1 className="font-display font-semibold text-2xl uppercase tracking-tight">Note unavailable</h1>
        <p className="text-slate-500 mt-2">This link has expired or sharing was turned off.</p>
        <Link to="/" className="tm-btn inline-flex mt-5">Go to TradesMate</Link>
      </div>
    );
  }

  const c = colorOf(note.color);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="note-public-page">
      <Seo title={note.title || 'Shared note'} noindex />
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 font-display font-semibold text-lg text-slate-900"><span className="w-7 h-7 rounded-full bg-brand text-brand-ink grid place-items-center"><HardHat size={14} /></span> TradesMate</span>
          <span className="text-xs uppercase tracking-widest text-slate-400 ml-auto font-semibold">Shared note</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="tm-card p-6" style={{ borderTop: `5px solid ${c.bar === 'transparent' ? '#0f172a' : c.bar}` }}>
          <div className="flex items-center gap-2 mb-1">
            {note.color && <span className="text-[11px] uppercase tracking-wide px-2 py-0.5 rounded font-semibold" style={{ color: c.dot, backgroundColor: c.dot + '22' }}>{c.label}</span>}
          </div>
          <h1 className="font-display font-semibold text-3xl uppercase tracking-tight mb-6">{note.title || 'Untitled note'}</h1>

          <div className="space-y-5">
            {(note.sections || []).map((s) => (
              <section key={s.id} data-testid={`public-section-${s.id}`}>
                {s.title && <h2 className="text-xs uppercase tracking-widest font-bold text-slate-500 border-b border-slate-200 pb-1 mb-2">{s.title}</h2>}
                <ul className="space-y-1.5">
                  {(s.items || []).map((it) => (
                    <li key={it.id} className="flex items-start gap-2 text-sm" data-testid={`public-item-${it.id}`}>
                      {it.type === 'todo' && (it.done
                        ? <CheckSquare size={17} className="text-emerald-600 shrink-0 mt-0.5" />
                        : <Square size={17} className="text-slate-400 shrink-0 mt-0.5" />)}
                      {it.type === 'bullet' && <span className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-brand" />}
                      <span className={`leading-relaxed ${it.done ? 'line-through text-slate-400' : 'text-slate-800'} ${it.type === 'text' ? 'ml-0' : ''}`}>{it.text || <span className="text-slate-300">—</span>}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-slate-500 text-sm">Made with <Link to="/" className="text-brand font-bold hover:underline">TradesMate</Link> — free calculators & job tools for UK tradesmen.</p>
        </div>
      </main>
    </div>
  );
}
